import { useMemo, useState, useEffect, useRef } from 'react';
import { addAction, addError } from '@shared/utils/logger';
import { isValidProperty, transformPropertiesToListings } from '@shared/utils/accommodations/AccommodationTiles';
import { isValidCoordinates } from '@shared/core/places';
import type { AccommodationCatalogUnitsOccupancyInput, SearchExtendedAccommodationPropertiesV2Payload } from '@graphql/generated-base';
import { useAccommodationsConsolePropertiesV2Query } from '../AccommodationsConsole.graphql.generated';
import { FetchStrategy } from '@graphql/generated-base';
import type { AccommodationsConsoleState, JoyHotelAccommodationListingDTO } from '@shared/types/Accommodations.types';
import { usePrioritizeTagsQueryParam } from './useAccommodationsConsoleSearchParamsState/usePrioritizeTagsQueryParam';
import { useExtendedAccommodationSearchMetadata } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { PriceSource } from '@shared/constants/accommodations';
import { isEqual } from 'lodash-es';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import { buildWalletSpendQueryVariables } from './walletSpendQueryVariables';
import { VENUE_SEARCH_RADIUS_KM } from './useAccommodationsConsoleSearchParamsState/constants';

interface UseAccommodationsConsoleListingsArgs
  extends Readonly<{
    isInitialLoad: boolean;
    state: AccommodationsConsoleState;
    eventHandle: Maybe<string>;
    /** Set only by the cash-fund variant; turns on each listing's server-computed fund/card split. */
    cashFund?: CashFundConfig;
    /**
     * How many sponsored slots this search asks the BE to fill (`maxSponsoredSlots`).
     *
     * `undefined` means OMIT the field, which leaves the server's contracted slot count in charge —
     * it is NOT the same as `0`, which asks for no sponsored properties at all. Only the scenarios
     * in the featured-placement experiment (`guest`, `admin`) pass a number; every other caller
     * leaves this undefined so sponsored placements keep working exactly as they do today.
     */
    maxSponsoredSlots?: number;
    /**
     * The page's own tags (`scenarioConfig.defaultPrioritizeTags`). They reach the URL through
     * useSeedDefaultPrioritizeTags's effect, which runs AFTER this hook has built its first
     * request, so a `/honeymoon` landing with no tag in the link would otherwise send that one
     * request as a plain prefer-cache search. Only the fetch strategy reads this; the tags
     * requested still come from the URL alone, so a guest unchecking the tag is honoured.
     */
    defaultPrioritizeTags?: ReadonlyArray<string>;
  }> {}

export type ConfirmedSearchPayload = Readonly<{
  location: Readonly<{
    latitude: number;
    longitude: number;
    context: {
      googlePlaceId: string | undefined;
    };
  }> | null;
  searchDates: Readonly<{
    checkin: string;
    checkout: string;
  }>;
  occupancies: AccommodationCatalogUnitsOccupancyInput;
}> | null;

/**
 * Determines if custom search params should be skipped.
 * When true, the query uses only eventHandle (shows event venue first).
 * When false, the query includes custom search params (location, dates, occupancies).
 */
const shouldSkipCustomSearchParams = (isInitialLoad: boolean, hasValidVenueLocation: boolean, hasCustomSearchLocation: boolean): boolean => {
  return isInitialLoad && hasValidVenueLocation && !hasCustomSearchLocation;
};

export const useRegionalHotels = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, isInitialLoad, eventHandle, cashFund, maxSponsoredSlots, defaultPrioritizeTags } = args;

  // Spread into `customSearchParams` rather than assigned, because the field must be ABSENT — not
  // present-and-zero — for every caller outside the featured-placement experiment. `0` is a real,
  // meaningful value there ("no sponsored properties"), so `maxSponsoredSlots: undefined` would not
  // do: an explicit null/undefined in the payload also changes the request's cache key and reads to
  // a reviewer as if the field were always sent.
  const sponsoredSlotsParam = useMemo(() => (maxSponsoredSlots === undefined ? {} : { maxSponsoredSlots }), [maxSponsoredSlots]);

  // The server returns `tags` on a property ONLY when the request asks for tag prioritization, so
  // this has to travel with the query — requesting the field alone yields [] for everything.
  // Read straight from the URL, the single source of truth (see useSeedDefaultPrioritizeTags), so
  // the tags requested and the tags sorted on cannot drift apart.
  const prioritizeTags = usePrioritizeTagsQueryParam();
  // A tag-filtered (honeymoon) search reaches across a whole destination, where dozens of hotels have
  // no content snapshot yet; fetching those live would hold up first paint, so it takes snapshots
  // only and leaves the gaps to useBackgroundPricingRefresh's prefer-vendor request. Guest venue
  // searches keep prefer-cache: their few cold misses are cheap, and bots (which never run the
  // refresh) still get the complete list.
  const isTagBoostedSurface = prioritizeTags.length > 0 || (defaultPrioritizeTags?.length ?? 0) > 0;
  const contentFetchStrategy = isTagBoostedSurface ? FetchStrategy.CACHE_ONLY : FetchStrategy.PREFER_CACHE;
  const { currency: currencyCode, loading: loadingTravelCurrencyAllowlist } = useTravelWhiteLabelCurrency();

  const [cachedListings, setCachedListings] = useState<Array<JoyHotelAccommodationListingDTO>>([]);
  const [hasReceivedRegionalData, setHasReceivedRegionalData] = useState(false);
  const [confirmedSearchPayload, setConfirmedSearchPayload] = useState<ConfirmedSearchPayload>(null);

  const { isPreparingSearchMetadata, searchMetadata } = useExtendedAccommodationSearchMetadata();

  const hasValidLocationSearchCoordinates = isValidCoordinates(state.circularRegionSearch);

  const locationPayload = useMemo(() => {
    if (!hasValidLocationSearchCoordinates) {
      return null;
    }
    return {
      latitude: state.circularRegionSearch?.latitude,
      longitude: state.circularRegionSearch?.longitude,
      context: {
        googlePlaceId: state.circularRegionSearch?.googlePlaceId,
      },
    };
  }, [hasValidLocationSearchCoordinates, state.circularRegionSearch?.latitude, state.circularRegionSearch?.longitude, state.circularRegionSearch?.googlePlaceId]);

  const hasValidVenueLocation = !!state.venue.location && isValidCoordinates({ latitude: state.venue.location.lat, longitude: state.venue.location.lng });

  const hasCustomSearchLocation = useMemo(() => {
    if (!hasValidLocationSearchCoordinates || !hasValidVenueLocation) {
      return false;
    }
    return state.venue.location?.lat !== state.circularRegionSearch?.latitude || state.venue.location?.lng !== state.circularRegionSearch?.longitude;
  }, [hasValidLocationSearchCoordinates, hasValidVenueLocation, state.venue.location, state.circularRegionSearch]);

  const skipCustomSearchParams = shouldSkipCustomSearchParams(isInitialLoad, hasValidVenueLocation, hasCustomSearchLocation);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  // Build payload conditionally - null when we can't build a valid payload
  const rawPayload = useMemo((): SearchExtendedAccommodationPropertiesV2Payload | null => {
    // No event handle is legitimate for a public search (the honeymoon page): the API treats the
    // event purely as a source of defaults. Such a search must supply its own location, which the
    // custom-params branch below already requires — so only the event-defaulted branch needs a
    // handle, and it is guarded separately.
    if (loadingTravelCurrencyAllowlist) {
      return null;
    }

    // When skipping custom search params, only include eventHandle (shows event venue first).
    // This branch IS the event-defaulted search, so it cannot run without a handle.
    if (skipCustomSearchParams) {
      if (!eventHandle) {
        return null;
      }
      return {
        eventHandle,
        customSearchParams: {
          currencyCode,
          useRealtimePricing: false,
          fetchStrategy: contentFetchStrategy,
          // Omitted entirely when empty so pages that request no tags keep their existing payload
          // shape, and with it their cache entries.
          ...(prioritizeTags.length ? { prioritizeTags: [...prioritizeTags] } : {}),
          ...sponsoredSlotsParam,
        },
      };
    }

    // When including custom search params, require valid location coordinates
    if (!hasValidLocationSearchCoordinates || !locationPayload || isPreparingSearchMetadata) {
      return null;
    }

    return {
      eventHandle,
      customSearchParams: {
        currencyCode,
        // Always the venue radius. A honeymoon search widens on the server: with `prioritizeTags`
        // set, travel_service returns ONLY the tagged hotels of the searched destination (or a
        // 50km circle) and ignores this radius.
        radiusKM: VENUE_SEARCH_RADIUS_KM,
        location: locationPayload,
        useRealtimePricing: false,
        fetchStrategy: contentFetchStrategy,
        stayDates: {
          checkIn: {
            high: state.searchDates.checkin,
            low: state.searchDates.checkin,
          },
          checkOut: {
            high: state.searchDates.checkout,
            low: state.searchDates.checkout,
          },
        },
        occupancies: state.occupancies,
        metadata: searchMetadata,
        ...(prioritizeTags.length ? { prioritizeTags: [...prioritizeTags] } : {}),
        ...sponsoredSlotsParam,
      },
    };
  }, [
    eventHandle,
    skipCustomSearchParams,
    hasValidLocationSearchCoordinates,
    currencyCode,
    locationPayload,
    isPreparingSearchMetadata,
    state.searchDates.checkin,
    state.searchDates.checkout,
    state.occupancies,
    searchMetadata,
    loadingTravelCurrencyAllowlist,
    prioritizeTags,
    contentFetchStrategy,
    sponsoredSlotsParam,
  ]);

  const payloadRef = useRef(rawPayload);

  if (!isEqual(payloadRef.current, rawPayload)) {
    payloadRef.current = rawPayload;
  }

  const payload = payloadRef.current;

  useEffect(() => {
    setConfirmedSearchPayload(null);
    setCachedListings([]);
    setHasReceivedRegionalData(false);
  }, [payload]);

  const {
    data,
    loading: isFetchingListings,
    error,
  } = useAccommodationsConsolePropertiesV2Query({
    variables: {
      payload: payload!,
      ...buildWalletSpendQueryVariables(cashFund),
    },
    onError: error => {
      addError(error, {
        source: 'Search extended accommodations properties with circularRegionSearch api failed (AccommodationsConsole)',
        context: 'searchExtendedAccommodationPropertiesV2',
        errorMessage: error.message,
        circularRegionSearch: state.circularRegionSearch,
        payload: payload,
      });
    },
    skip: !payload || isBotClient(),
    batchMode: 'off',
    errorPolicy: 'all', // Returns partial data alongside errors instead of failing completely
    ssr: false, // TRAV-2939: Skip during SSR — skeleton UI provides a good loading experience
  });

  useEffect(() => {
    if (data === undefined && !error) {
      return;
    }

    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (properties) {
      const validProperties = properties.filter(isValidProperty);
      // Snapshot content (prefer-cache, or cache-only for tagged searches) with useRealtimePricing false, so these prices come from travel_rate_snapshot.
      setCachedListings(transformPropertiesToListings(validProperties, mapHotelRoomPrice, PriceSource.SNAPSHOT));

      if (validProperties.length === 0) {
        addAction('Search extended accommodations properties with circularRegionSearch api with empty results (AccommodationsConsole)', {
          context: 'searchExtendedAccommodationProperties',
          circularRegionSearch: state.circularRegionSearch,
        });
      }
    }

    setHasReceivedRegionalData(true);
  }, [data, error, mapHotelRoomPrice]); // eslint-disable-line react-hooks/exhaustive-deps -- state.circularRegionSearch is only used for logging context

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    const customParams = payload?.customSearchParams;
    if (properties && customParams?.stayDates && customParams?.occupancies) {
      setConfirmedSearchPayload({
        occupancies: customParams.occupancies,
        // Mapped field by field rather than passed through: the GraphQL input allows a null/absent
        // `context`, while ConfirmedSearchPayload wants the object with an optional id inside it.
        location: customParams.location
          ? {
              latitude: customParams.location.latitude,
              longitude: customParams.location.longitude,
              context: { googlePlaceId: customParams.location.context?.googlePlaceId ?? undefined },
            }
          : null,
        searchDates: {
          checkin: customParams.stayDates.checkIn.low,
          checkout: customParams.stayDates.checkOut.low,
        },
      });
    }
  }, [data, payload]);

  return {
    cachedListings,
    hasReceivedRegionalData,
    isFetchingListings: isFetchingListings || loadingTravelCurrencyAllowlist,
    error: cachedListings.length === 0 ? error : undefined,
    confirmedSearchPayload,
    payload: payload as SearchExtendedAccommodationPropertiesV2Payload,
  };
};
